import React from 'react';
import { observer, inject } from 'mobx-react';
import rootStore, { IMobxRootState } from '../../RootStore';
import { isHeadlessMode } from '../../services/configService';
import {
    BILATERAL_RISK_VIEWS,
    ICWChangeNotification,
    ExternalDataItemID,
    VIEW,
} from '../../interfaces';
import {
    GLOBAL_INDEXES,
    IActiveCountry,
    RISK_CHART_MODES,
} from '../../services/APIService';
import { MACROECONOMICS_INDICATORS } from '../../constants';
import CancelableEvents from '../../utils/CancelableEvents';
import EventsService from '../../services/EventsService';
import ExternalDataPopup from '../GQexternalDataPopup/ExternalDataPopup';
import chartStore, { COMPOSITE_DATA_INTERVAL as COMPOSITE_DATA_INTERVAL } from './ChartStore';
import GQFlag from '../GQFlag';
import ActiveWeight from '../ActiveWeight';
import cx from 'classnames';
import { first, isEmpty } from 'lodash/fp';
import { FGIcons, FGStack, FGTile, FGTypography, FGSegmentedControl, TOOLTIP_PLACEMENT } from '@fg/design-system';
import { translateGlobalIndex } from '../../utils/renderUtils';
import { isViewType } from '../../utils/generalUtils';
import ChartExportButton from './ChartExportButton';
import ChartExportPreviewButton from './ChartExportPreviewButton';
import MethodologiesButton from 'components/MethodologiesButton';
import CountryStore from 'stores/CountryStore';

type Props = {
    currentCountryList?: number[];
    showLines?: boolean;
    showCircles?: boolean;
    currencyOn?: boolean;
    projectionDataOn?: boolean;
    riskChartMode?: RISK_CHART_MODES;
    showGQLine?: boolean;
    annoMode?: boolean;
    activePreset?: ICWChangeNotification;
    virtualTodayOn?: boolean;
    activeGlobalIndicators?: GLOBAL_INDEXES[];
    toggleGlobalIndicator?: (g: GLOBAL_INDEXES) => void;
    hideActivePreset?: boolean;
    countriesArr?: IActiveCountry[];
    hasActiveCustomWeight?: boolean;
    currencyCountry?: number[];
    macroeconomicsCountry?: number[];
    activeMacroeconomicsIndicator?: string;
    title: string;
    compositeDataInterval?: COMPOSITE_DATA_INTERVAL;
    preMonthlyRiskChartMode?: RISK_CHART_MODES;
    preMonthlyProjectionDataOn?: boolean;
    hideMonthlyToggle?: boolean;
    isExportComponent?: boolean;
    isRiskMarkersOn?: boolean;
    hideExportButton?: boolean;
    hideCompareButton?: boolean;
};

interface State {
    showOilDropdown: boolean;
    selectedExternalDataList: number[]; // selected data  for external data popup - custom weight, currency, gdp, oil
    showExternalDataDropdown: boolean; // if external data popup should be displayed
    externalDropDownList: any; // list for external data popup dropdown - country list/ oil indictaor list...
    externalDropDownSelectedList: any[]; // selected list in external data popup dropdown
    externalDDselectedItem?: any;
    macroeconomicsSelectedIndicator: string; // Tracks which macroeconomics indicator (GDP Growth or CPI Inflation) user selected
}

const COMMODITY_INDICATORS = ['oil_price', 'gold_price'];

@inject(
    ({
        chartStore,
        countryStore,
        risksStore,
        insightsStore,
        customWeightStore,
    }: IMobxRootState): Partial<Props> => {
        return {
            currentCountryList: countryStore.currentCountryList,
            showLines: chartStore.showLines,
            showCircles:
                chartStore.showCircles &&
                !customWeightStore.hasActiveCustomWeight,
            currencyOn: risksStore.currencyOn,
            projectionDataOn:
                risksStore.projectionDataOn &&
                !customWeightStore.hasActiveCustomWeight,
            riskChartMode: risksStore.riskChartMode,
            showGQLine: risksStore.showGeoquantLine,
            annoMode: insightsStore.annotationMode,
            activePreset: customWeightStore.activePreset,
            virtualTodayOn: chartStore.showVirtualToday,
            activeGlobalIndicators: risksStore.activeGlobalIndicators,
            toggleGlobalIndicator(g: GLOBAL_INDEXES) {
                risksStore.toggleGlobalIndicator(g);
            },
            countriesArr: countryStore.countries.countriesArr,
            hasActiveCustomWeight: customWeightStore.hasActiveCustomWeight,
            currencyCountry: risksStore.getCurrencyCountry(),
            macroeconomicsCountry: risksStore.macroeconomicsCountry,
            activeMacroeconomicsIndicator:
                risksStore.activeMacroeconomicsIndicator,
            compositeDataInterval: chartStore.compositeDataInterval,
            preMonthlyRiskChartMode: chartStore.preMonthlyRiskChartMode,
            preMonthlyProjectionDataOn: chartStore.preMonthlyProjectionDataOn,
            isRiskMarkersOn: risksStore.riskMarkersOn,
        };
    }
)
//todo: rename since now it has become both a Chart and a Map toolbar
@observer
class ChartToolbar extends React.Component<Props, State> {
    private cancelable = new CancelableEvents();
    private externalNode: Node;

    private getIndicatorDisplayName = (indicator: string): string => {
        return translateGlobalIndex[indicator] || indicator;
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            showExternalDataDropdown: false,
            showOilDropdown: false,
            selectedExternalDataList: [],
            externalDropDownList: [],
            externalDropDownSelectedList: [],
            externalDDselectedItem: [],
            macroeconomicsSelectedIndicator: '',
        };

        this.toggleVirtualTodayIf();
    }

    public toggleVirtualTodayIf = () => {
        if  (
            isViewType(VIEW.RISK_VIEW) ||
            isViewType(VIEW.SORT_BY_RISK) ||
            isViewType(VIEW.SORT_BY_DELTA) ||
            isViewType(VIEW.WORLD_GRAPH) ||
            isViewType(VIEW.CONTINGENT_GRAPH)
        ) {
            rootStore.chartStore.setState({
                showVirtualToday: true,
            });
        }
    };
    public handleClickOutside = (e: any) => {
        if (
            this.externalNode &&
            !this.externalNode.contains(e.target as Node)
        ) {
            this.setState({
                showExternalDataDropdown: false,
            });
        }
    };

    public componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside, true);
        this.cancelable.addEventSubscription(
            EventsService.registerOnMouseLeftClick(() => {
                if (
                    this.state.showExternalDataDropdown &&
                    this.state.selectedExternalDataList.length > 1
                ) {
                    this.setState({
                        showExternalDataDropdown: false,
                    });
                }
            })
        );
        if (!isEmpty(this.props.currencyCountry)) {
            this.setState({
                selectedExternalDataList: [ExternalDataItemID.CURRENCY],
                externalDropDownSelectedList: this.props.currencyCountry,
                externalDropDownList: this.props.countriesArr,
                externalDDselectedItem: this.props.countriesArr.find(
                    country => country.id === first(this.props.currencyCountry)
                ),
            });
            return;
        }
        if (!isEmpty(this.props.macroeconomicsCountry)) {
            this.setState({
                selectedExternalDataList: [ExternalDataItemID.MACROECONOMICS],
                macroeconomicsSelectedIndicator: this.props
                    .activeMacroeconomicsIndicator,
                externalDropDownSelectedList: this.props.macroeconomicsCountry,
                externalDropDownList: this.props.countriesArr,
                externalDDselectedItem: this.props.countriesArr.find(
                    country =>
                        country.id === first(this.props.macroeconomicsCountry)
                ),
            });
            return;
        }
        if (!isEmpty(this.props.activeGlobalIndicators)) {
            this.setState({
                selectedExternalDataList: [ExternalDataItemID.COMMODITY],
                externalDropDownSelectedList: this.props.activeGlobalIndicators,
                externalDropDownList: COMMODITY_INDICATORS,
            });
            return;
        }
    }

    public componentWillUnmount() {
        this.cancelable.cancelAll();
    }

    public componentDidUpdate() {
        if (this.state.externalDropDownSelectedList.length === 0) {
            if (rootStore.risksStore.currencyOn) {
                if (this.props.countriesArr) {
                    const tempSelectedId = rootStore.risksStore.getCurrencyCountry();
                    const filteredList = this.props.countriesArr.filter(
                        (item: any) => item.id === tempSelectedId[0]
                    );
                    this.setState({
                        selectedExternalDataList: [ExternalDataItemID.CURRENCY],
                        externalDropDownList: this.props.countriesArr,
                        externalDropDownSelectedList: rootStore.risksStore.getCurrencyCountry(),
                        externalDDselectedItem: filteredList[0],
                    });
                }
            }
            if (rootStore.risksStore.activeGlobalIndicators.length > 0) {
                this.setState({
                    selectedExternalDataList: [ExternalDataItemID.COMMODITY],
                    externalDropDownList: COMMODITY_INDICATORS,
                    externalDropDownSelectedList:
                        rootStore.risksStore.activeGlobalIndicators,
                });
            }
        } else if (
            this.state.externalDropDownSelectedList.length > 0 &&
            rootStore.risksStore.activeGlobalIndicators.length === 0 &&
            rootStore.risksStore.getCurrencyCountry().length === 0
        ) {
            this.setState({
                selectedExternalDataList: [],
                externalDropDownSelectedList: [],
                externalDDselectedItem: '',
            });
        }
    }

    public render() {
        if (isHeadlessMode()) {
            return null;
        }
        // In insights mode - export is disabled, user has the snapshot tool in the editor instead.
        const showExportBtn = !isViewType(VIEW.CREATE_INSIGHT) && !this.props.hideExportButton;

        // For debugging. When preview button is clicked, it opens the export template URL (/#/export_chart) in a new tab,
        // with the current data sent as query params. Set true to enable.
        const showExportPreviewButton = false;

        const isBilateralView = BILATERAL_RISK_VIEWS.some(view => isViewType(view));
        const hasCountriesSelected = isBilateralView
            ? CountryStore.currentCountryList.length > 1
            : CountryStore.currentCountryList.length > 0;
        
        return (
            <div className="chart-toolbar-buttons">
                {!this.props.hideActivePreset &&
                    this.props.activePreset &&
                    this.props.activePreset.activePreset && (
                        <div className="chart-toolbar-buttons-active-weight-container">
                            <ActiveWeight
                                active={this.props.showGQLine}
                                onClick={this.toggleCustomWeight}
                                preset={this.props.activePreset}
                            />
                        </div>
                    )}
                <div
                    className="chart-header-container"
                    ref={node => (this.externalNode = node)}>
                    <div style={{ flex: 1 }}>
                        <FGTypography
                            tag="h4"
                            variant="headline4"
                            color="strong">
                            {this.props.title}
                        </FGTypography>
                    </div>
                    <div
                        style={{
                            flex: 1,
                            display: 'flex',
                            gap: 8,
                            justifyContent: 'flex-end',
                        }}>
                        {!this.props.hideMonthlyToggle &&
                            this.renderMonthlyToggle()}
                        {!this.props.isExportComponent && (
                            <>
                                {!this.props.hideCompareButton &&
                                    this.renderCompareWithContainer()}
                                {showExportBtn && <ChartExportButton hasCountriesSelected={hasCountriesSelected} />}
                                {showExportPreviewButton && <ChartExportPreviewButton hasCountriesSelected={CountryStore.currentCountryList.length > 0}/>}
                                <MethodologiesButton />
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    private renderMonthlyToggle = () => {
        return (
            <FGSegmentedControl
                className={cx('daily-monthly-segmented-control', {
                    'monthly-disabled': this.props.isRiskMarkersOn,
                })}
                selectedValue={
                    this.props.compositeDataInterval ==
                    COMPOSITE_DATA_INTERVAL.MONTHLY
                        ? 'monthly'
                        : 'daily'
                }
                name="Monthly/Daily"
                onChange={val => {
                    const goingMonthly = val === 'monthly';
                    chartStore.setState({
                        compositeDataInterval: goingMonthly
                            ? COMPOSITE_DATA_INTERVAL.MONTHLY
                            : COMPOSITE_DATA_INTERVAL.DAILY,
                    });
                    if (goingMonthly) {
                        if (this.props.isRiskMarkersOn) {
                            return;
                        }
                        // Save current selections so they can be restored when the
                        // user switches back to Daily. Pulse and Past Projections
                        // are unavailable for monthly data, so they get force-reset
                        // here — without this snapshot the user's prior selection
                        // would be lost permanently.
                        chartStore.setState({
                            preMonthlyRiskChartMode:
                                rootStore.risksStore.riskChartMode,
                            preMonthlyProjectionDataOn:
                                rootStore.risksStore.projectionDataOn,
                        });
                        rootStore.risksStore.riskChartMode =
                            RISK_CHART_MODES.RISK;
                        rootStore.risksStore.projectionDataOn = false;
                        // Note: do not reset "Compare with" external data selection here.
                        // External data is shown in its original (daily) resolution
                        // overlaid on top of the monthly GQ data.
                    } else {
                        // Switching back to Daily — restore the selections that
                        // were active before the user entered Monthly mode.
                        if (this.props.preMonthlyRiskChartMode != null) {
                            rootStore.risksStore.riskChartMode =
                                this.props.preMonthlyRiskChartMode;
                        }
                        if (this.props.preMonthlyProjectionDataOn != null) {
                            rootStore.risksStore.projectionDataOn =
                                this.props.preMonthlyProjectionDataOn;
                        }
                        chartStore.setState({
                            preMonthlyRiskChartMode: null,
                            preMonthlyProjectionDataOn: null,
                        });
                    }
                }}
                segments={[
                    {
                        label: 'Daily',
                        value: 'daily',
                        tooltip: {
                            placement: TOOLTIP_PLACEMENT.top,
                            content: (
                                <FGTypography variant="body4" tag="label">
                                    Daily score shows the value for each
                                    individual day.
                                </FGTypography>
                            ),
                        },
                    },
                    {
                        label: 'Monthly',
                        value: 'monthly',
                        tooltip: {
                            placement: TOOLTIP_PLACEMENT.top,
                            content: (
                                <FGTypography variant="body4" tag="label">
                                    Monthly score shows the value on the last
                                    day of that month.
                                </FGTypography>
                            ),
                        },
                    },
                ]}
            />
        );
    };


    private renderExternalDropdown() {
        return (
            <FGTile
                padding="small"
                appearance="secondary"
                alignItems="center"
                style={{ overflow: 'unset', borderRadius: 3 }}>
                <FGStack
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ width: '100%' }}>
                    <div className="selected-compare-item-container">
                        {this.state.externalDDselectedItem &&
                            this.state.externalDDselectedItem !== '' &&
                            this.state.selectedExternalDataList[0] ===
                                ExternalDataItemID.CURRENCY && (
                                <div className="selected-compare-item">
                                    <GQFlag
                                        countryId={
                                            this.state.externalDDselectedItem.id
                                        }
                                        className="flag-icon-squared"
                                    />
                                    <span>
                                        {
                                            this.state.externalDDselectedItem
                                                .currency_code
                                        }
                                    </span>
                                </div>
                            )}
                        {this.state.selectedExternalDataList[0] ===
                            ExternalDataItemID.COMMODITY && (
                            <div className="selected-compare-item commodity">
                                <span>
                                    {this.state.externalDropDownSelectedList[0].replace(
                                        /_/g,
                                        ' '
                                    )}
                                </span>
                            </div>
                        )}
                        {this.state.selectedExternalDataList[0] ===
                            ExternalDataItemID.MACROECONOMICS && (
                            <div className="selected-compare-item macroeconomics">
                                {!isEmpty(this.state.externalDDselectedItem) ? (
                                    <div className="selected-compare-item">
                                        <span>
                                            {this.getIndicatorDisplayName(
                                                this.state
                                                    .macroeconomicsSelectedIndicator
                                            )}{' '}
                                        </span>
                                        <span>
                                            (
                                            {
                                                this.state
                                                    .externalDDselectedItem
                                                    .abbreviation
                                            }
                                            )
                                        </span>
                                    </div>
                                ) : (
                                    this.state
                                        .macroeconomicsSelectedIndicator && (
                                        <span>Select Category</span>
                                    )
                                )}
                            </div>
                        )}
                        <div
                            className="delete-selected-popup-data"
                            onClick={e => {
                                e.stopPropagation();
                                this.resetExternalDataSelection();
                            }}>
                            <FGIcons
                                icon="Remove"
                                size={12}
                                aria-label="Remove"
                            />
                        </div>
                    </div>
                </FGStack>
            </FGTile>
        );
    }

    private toggleCurrency = (id: number) => {
        this.handleSelectExternalData(id);
        this.setState({
            externalDropDownList: this.props.countriesArr,
        });
    };

    private toggleCommodity = (id: number) => {
        this.handleSelectExternalData(id);
        this.setState({
            externalDropDownList: COMMODITY_INDICATORS,
        });
    };

    private toggleMacroeconomics = (id: number) => {
        this.handleSelectExternalData(id);
        this.setState({
            externalDropDownList: MACROECONOMICS_INDICATORS,
        });
    }

    private resetExternalDataSelection = () => {
        rootStore.risksStore.cleanMacroeconomicsCountry();
        rootStore.risksStore.clearGlobalIndicator();
        rootStore.risksStore.cleanCurrencyCountry();
        this.setState({
            selectedExternalDataList: [],
            externalDropDownList: [],
            externalDropDownSelectedList: [],
            externalDDselectedItem: '',
            macroeconomicsSelectedIndicator: '',
        });
    }

    private handleSelectExternalData = (id: number) => {
        const {
            selectedExternalDataList,
            externalDropDownSelectedList,
        } = this.state;
        const tempList = selectedExternalDataList.concat();
        if (selectedExternalDataList.includes(id)) {
            tempList.pop();
        } else {
            tempList[0] = id;
        }
        if (!isEmpty(externalDropDownSelectedList)) {
            this.externalDropDownItemClicked(externalDropDownSelectedList[0]);
        }

        const isInCountryPickerStage =
            this.state.macroeconomicsSelectedIndicator && this.state.externalDropDownList === this.props.countriesArr;

        if (isInCountryPickerStage) {
            rootStore.risksStore.cleanMacroeconomicsCountry();
            rootStore.risksStore.clearGlobalIndicator();
            this.setState({
                selectedExternalDataList: [ExternalDataItemID.MACROECONOMICS],
                externalDropDownList: MACROECONOMICS_INDICATORS,
                externalDropDownSelectedList: [],
                externalDDselectedItem: '',
                macroeconomicsSelectedIndicator: '',
            });
            return;
        }

        this.setState({
            selectedExternalDataList: tempList,
            externalDropDownSelectedList: [],
            externalDDselectedItem: '',
        });
    };

    private openExternalDataDropdown = (
        e: React.MouseEvent<HTMLDivElement>
    ) => {
        e.stopPropagation();
        this.setState({
            showExternalDataDropdown: !this.state.showExternalDataDropdown,
        });
    };

    private externalDropDownItemClicked = (id: any) => {
        const filteredList = this.state.externalDropDownList.filter(
            (item: any) => item.id === id
        );
        switch (this.state.selectedExternalDataList[0]) {
            case ExternalDataItemID.CUSTOM_WEIGHT:
                break;
            case ExternalDataItemID.CURRENCY:
                rootStore.risksStore.setCurrencyCountry(id);
                this.setState({
                    externalDropDownSelectedList: rootStore.risksStore.getCurrencyCountry(),
                    externalDDselectedItem: filteredList[0],
                });
                break;
            case ExternalDataItemID.GDP:
                break;
            case ExternalDataItemID.COMMODITY:
                rootStore.risksStore.toggleGlobalIndicator(id);
                this.setState({
                    externalDropDownSelectedList: [id],
                });
                break;
            case ExternalDataItemID.MACROECONOMICS:
                // When user selects the subcategory we will get a string
                // otherwise on country selection it will be a number
                if (typeof id === 'string') {
                    this.setState({
                        macroeconomicsSelectedIndicator: id,
                        externalDropDownList: this.props.countriesArr,
                        externalDropDownSelectedList: [],
                        externalDDselectedItem: '',
                    });
                } else if (typeof id === 'number') {
                    const filteredCountryForMacro = this.props.countriesArr?.find(item => item.id === id);
                    if (rootStore.risksStore.activeGlobalIndicators.includes(this.state.macroeconomicsSelectedIndicator as any)) {
                        rootStore.risksStore.clearGlobalIndicator();
                    }
                    rootStore.risksStore.setMacroeconomicsCountry(id, this.state.macroeconomicsSelectedIndicator);
                    rootStore.risksStore.toggleGlobalIndicator(this.state.macroeconomicsSelectedIndicator as any);
                    this.setState({
                        externalDropDownSelectedList: [id],
                        externalDDselectedItem: filteredCountryForMacro,
                    });
                }
                break;
        }
    };

    private toggleCustomWeight = () => {
        rootStore.risksStore.riskChartMode =
            this.props.riskChartMode === RISK_CHART_MODES.RISK_AND_PULSE
                ? RISK_CHART_MODES.PULSE
                : RISK_CHART_MODES.RISK_AND_PULSE;
    };

    private renderCompareWithContainer = () => {
        return (
            <div className="compare-with-button-container">
                <div
                    className={cx('compare-with-button', {
                        open: this.state.showExternalDataDropdown,
                    })}
                    onClick={this.openExternalDataDropdown}>
                    {!isEmpty(this.state.externalDropDownSelectedList) ||
                    (this.state.selectedExternalDataList[0] ===
                        ExternalDataItemID.MACROECONOMICS &&
                        this.state.macroeconomicsSelectedIndicator) ? (
                        this.renderExternalDropdown()
                    ) : (
                        <FGTile
                            padding="small"
                            appearance="secondary"
                            alignItems="center"
                            style={{ overflow: 'unset', borderRadius: 3 }}>
                            <FGStack
                                justifyContent="space-between"
                                alignItems='center'
                                style={{ width: '100%' }}>
                                <FGTypography
                                    tag="label"
                                    variant="label3"
                                    color="weak">
                                    Compare with
                                </FGTypography>
                                <FGIcons
                                    icon={
                                        this.state.showExternalDataDropdown
                                            ? 'Expanded'
                                            : 'Collapsed'
                                    }
                                />
                            </FGStack>
                        </FGTile>
                    )}
                </div>
                {this.state.showExternalDataDropdown && (
                    <ExternalDataPopup
                        itemsList={[
                            {
                                name: 'Currency',
                                id: ExternalDataItemID.CURRENCY,
                                onClick: this.toggleCurrency,
                            },
                            {
                                name: 'Commodity',
                                id: ExternalDataItemID.COMMODITY,
                                onClick: this.toggleCommodity,
                            },
                            {
                                name: 'Macroeconomics',
                                id: ExternalDataItemID.MACROECONOMICS,
                                onClick: this.toggleMacroeconomics,
                            },
                        ]}
                        selectedList={this.state.selectedExternalDataList}
                        dropDownList={this.state.externalDropDownList}
                        dropDownSelectedItems={
                            this.state.externalDropDownSelectedList
                        }
                        onDropDownItemClicked={id => {
                            this.externalDropDownItemClicked(id);
                        }}
                        macroeconomicsSelectedIndicator={this.getIndicatorDisplayName(
                            this.state.macroeconomicsSelectedIndicator
                        )}
                    />
                )}
            </div>
        );

    }   
}


export default ChartToolbar;
